import { createContext, type ReactNode, useContext, useMemo } from "react";

import type { ApiError } from "./client";
import { useResource } from "./useResource";
import type { ApiWorkspace } from "./workspace";

// Which workspace the dashboard is acting in.
//
// A deployment can hold more than one — the schema has always allowed it, and
// `POST /workspaces` and CLI enrolment both reach it — but nothing in the
// product offers a choice yet, so the answer is the caller's first membership.
// That is the same answer eleven components used to reach for individually, by
// reading `workspaces[0]` off `GET /workspaces`.
//
// The point of gathering it here is not the fetch. It is that "which workspace"
// becomes one decision with one place to change: a switcher, a URL segment, a
// remembered preference — each of those edits this file and nothing else.
// Spread across the call sites, every one of them was a place that could
// disagree with the others about what the current workspace was.
//
// See `work-in-progress/multiple-workspaces.md` §Phasing.

export type CurrentWorkspaceValue = {
  readonly error: ApiError | undefined;
  /**
   * Whether the caller may manage this workspace — `owner` or `admin`.
   *
   * Four components asked this by comparing the role against both strings
   * themselves. It is one derived fact, and the kind that is wrong quietly:
   * a check that forgets `owner` hides an affordance from the one person who
   * certainly has it.
   */
  readonly isAdmin: boolean;
  readonly loading: boolean;
  readonly workspace: ApiWorkspace | null;
  /** The public id, which is what every workspace-scoped call takes. */
  readonly workspaceId: string | null;
};

const CurrentWorkspaceContext = createContext<CurrentWorkspaceValue | null>(
  null,
);

export const CurrentWorkspaceProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const { data, error, loading } =
    useResource<ReadonlyArray<ApiWorkspace>>("/workspaces");

  const value = useMemo<CurrentWorkspaceValue>(() => {
    // The first membership, ordered by the server (oldest first). Which one is
    // "current" is exactly the decision a switcher would take over.
    const workspace = data?.[0] ?? null;
    return {
      error,
      isAdmin: workspace?.role === "admin" || workspace?.role === "owner",
      loading,
      workspace,
      workspaceId: workspace?.public_id ?? null,
    };
  }, [data, error, loading]);

  return (
    <CurrentWorkspaceContext.Provider value={value}>
      {children}
    </CurrentWorkspaceContext.Provider>
  );
};

export const useCurrentWorkspace = (): CurrentWorkspaceValue => {
  const value = useContext(CurrentWorkspaceContext);
  if (!value) {
    throw new Error(
      "useCurrentWorkspace must be used within a CurrentWorkspaceProvider",
    );
  }
  return value;
};
